import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { API, BACKEND_URL } from "@/lib/api";

export default function JournalPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    API.get("/journey/reflections").then(res => {
      setData(res.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white">
        <div className="w-10 h-10 border-4 border-[#A0655A] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  // Group reflections by layer
  const grouped = {};
  data?.reflections?.forEach(r => {
    if (!grouped[r.layer_id]) {
      grouped[r.layer_id] = {
        layer_title: r.layer_title,
        movement_title: r.movement_title,
        items: []
      };
    }
    grouped[r.layer_id].items.push(r);
  });

  const hasReflections = data?.reflections?.length > 0;
  const hasCheckin = data?.checkin?.completed;

  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      
      <div className="pt-24 sm:pt-28 pb-8 sm:pb-20 px-4 sm:px-6">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <h1 className="heading-font text-3xl sm:text-4xl font-medium text-[#0A0A0A] mb-3" data-testid="reflections-page-title">
              Min resa hittills
            </h1>
            <p className="text-[#0A0A0A]/60 mb-4">
              Alla dina reflektioner och tankar samlade på ett ställe.
            </p>
            {(hasCheckin || hasReflections) && (
              <Button
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = `${BACKEND_URL}/api/journey/export-pdf`;
                  link.download = 'min-resa-emotionell-frihet.pdf';
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                }}
                variant="outline"
                className="rounded-full text-sm"
                data-testid="export-pdf-btn"
              >
                <BookOpen size={14} className="mr-2" /> Ladda ner som PDF
              </Button>
            )}
          </motion.div>

          {/* Check-in section */}
          {hasCheckin && (
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="mb-10"
            >
              <div className="bg-white border border-[#EBE3DF] rounded-2xl p-6">
                <p className="text-sm text-[#A0655A] font-medium uppercase tracking-wide mb-4">Min incheckning</p>
                
                {data.checkin.intention && (
                  <div className="mb-6">
                    <p className="text-sm text-[#0A0A0A]/50 mb-2">Mina förhoppningar:</p>
                    <p className="text-[#0A0A0A] italic leading-relaxed">"{data.checkin.intention}"</p>
                  </div>
                )}

                {data.checkin.answers && Object.entries(data.checkin.answers).map(([key, answer]) => {
                  const LABELS = {
                    "hopes": "Med hjälp av den här metoden skulle jag vilja...",
                    "struggles": "Det jag har mest problem med idag är...",
                    "goals": "När jag är klar med resan hoppas jag att jag...",
                    "0": "Hur skulle du beskriva din relation till dig själv just nu?",
                    "1": "Finns det känslor eller mönster du märker att du ofta undviker?",
                    "2": "Vad önskar du att du kunde förändra i hur du mår eller lever?"
                  };
                  return answer ? (
                    <div key={key} className="mb-4 last:mb-0">
                      <p className="text-sm text-[#0A0A0A]/50 mb-1">{LABELS[key] || key}</p>
                      <p className="text-[#0A0A0A] leading-relaxed">{answer}</p>
                    </div>
                  ) : null;
                })}
              </div>
            </motion.div>
          )}

          {/* Reflections grouped by layer */}
          {hasReflections ? (
            <div className="space-y-8">
              {Object.entries(grouped).map(([layerId, group], gIdx) => (
                <motion.div
                  key={layerId}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15 + gIdx * 0.05 }}
                >
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-8 h-8 rounded-full bg-[#A0655A] flex items-center justify-center">
                      <Check className="text-white" size={14} />
                    </div>
                    <div>
                      <p className="font-medium text-[#0A0A0A]">{group.layer_title}</p>
                      <p className="text-xs text-[#A0655A]">{group.movement_title}</p>
                    </div>
                  </div>

                  <div className="ml-4 pl-7 border-l-2 border-[#EBE3DF] space-y-4">
                    {group.items.map((r, rIdx) => (
                      <div key={rIdx} className="bg-white border border-[#EBE3DF] rounded-xl p-5">
                        <p className="text-sm text-[#0A0A0A]/50 mb-2">{r.question}</p>
                        <p className="text-[#0A0A0A] leading-relaxed whitespace-pre-wrap">{r.content}</p>
                        <p className="text-xs text-[#0A0A0A]/30 mt-3">
                          {new Date(r.updated_at).toLocaleDateString('sv-SE')}
                        </p>
                      </div>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <p className="text-[#0A0A0A]/50 mb-4">Du har inga sparade reflektioner än.</p>
              <Button 
                onClick={() => window.location.href = '/dashboard'}
                className="bg-[#A0655A] hover:bg-[#8B5549] text-white rounded-full"
              >
                Gå till din resa
              </Button>
            </div>
          )}
        </div>
      </div>

      <Footer />
    </div>
  );
}
